import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, member, type PublicTeamSummary } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import type { Game, LiveStatus, TeamEvent } from '../types';
import LivePlayer from '../components/LivePlayer';
import GameCard from '../components/GameCard';
import { PlayerWithOverlay } from '../components/ScoreOverlay';
import FollowButton from '../components/FollowButton';
import { Skeleton } from '../components/PageChrome';
import { Heading, Subheading } from '../ui/heading';
import { Text, TextLink } from '../ui/text';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export default function TeamHome() {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuth();

  const [team, setTeam] = useState<PublicTeamSummary | undefined>();
  const [live, setLive] = useState<LiveStatus | undefined>();
  const [livePlaybackUrl, setLivePlaybackUrl] = useState<string | undefined>();
  const [liveTokenExpiresAt, setLiveTokenExpiresAt] = useState<string | undefined>();
  const [liveError, setLiveError] = useState<string | undefined>();
  const [games, setGames] = useState<Game[]>();
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.listTeamsPublic();
        const found = data.teams.find((t) => t.teamId === teamId);
        if (!cancelled) {
          if (found) setTeam(found);
          else setError(`Team not found: ${teamId}`);
        }
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  useEffect(() => {
    if (!teamId || !user) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await member.events(teamId);
        if (!cancelled) setEvents(res.events);
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, user]);

  // Live status poll.
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await api.liveStatus(teamId);
        if (!cancelled) setLive(status);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    };
    void tick();
    const id = window.setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [teamId]);

  // Whenever live status flips, re-evaluate whether to fetch a token.
  useEffect(() => {
    if (!live?.isLive) {
      setLivePlaybackUrl(undefined);
      setLiveTokenExpiresAt(undefined);
      setLiveError(undefined);
      return;
    }
    // PRIVATE + signed out → don't try to fetch; the UI shows a sign-in CTA.
    if (live.livePrivacy === 'PRIVATE' && !user) {
      setLivePlaybackUrl(undefined);
      setLiveTokenExpiresAt(undefined);
      setLiveError(undefined);
      return;
    }
    if (!teamId) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await api.liveToken(teamId);
        if (!cancelled) {
          setLivePlaybackUrl(token.playbackUrl);
          setLiveTokenExpiresAt(token.expiresAt);
        }
      } catch (err) {
        if (!cancelled) {
          setLiveError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [live?.isLive, live?.gameId, live?.livePrivacy, user, teamId]);

  useEffect(() => {
    if (!teamId || !live?.isLive || !liveTokenExpiresAt) return;
    const refreshInMs = Math.max(
      15_000,
      new Date(liveTokenExpiresAt).getTime() - Date.now() - 60_000,
    );
    const id = window.setTimeout(() => {
      void (async () => {
        try {
          const token = await api.liveToken(teamId);
          setLivePlaybackUrl(token.playbackUrl);
          setLiveTokenExpiresAt(token.expiresAt);
          setLiveError(undefined);
        } catch (err) {
          setLiveError(err instanceof Error ? err.message : String(err));
        }
      })();
    }, refreshInMs);
    return () => window.clearTimeout(id);
  }, [live?.isLive, liveTokenExpiresAt, teamId]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.listGames(teamId);
        if (!cancelled) setGames(res.games);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const loadingGames = games === undefined;
  const upcoming = useMemo(
    () =>
      games
        ? [...games]
            .filter((g) => g.status === 'SCHEDULED')
            .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
        : [],
    [games],
  );
  const past = useMemo(
    () =>
      games
        ? [...games]
            .filter((g) => g.status === 'READY')
            .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt))
        : [],
    [games],
  );
  const nextGame = upcoming[0];
  const liveGame = useMemo(
    () => (live?.gameId && games ? games.find((g) => g.gameId === live.gameId) : undefined),
    [games, live?.gameId],
  );

  const renderLive = () => {
    if (!live?.isLive) {
      return (
        <div className="overflow-hidden rounded-lg border border-dashed border-zinc-800 bg-zinc-900">
          <div className="bg-[linear-gradient(135deg,rgba(16,185,129,0.16),rgba(39,39,42,0.18)_45%,rgba(14,116,144,0.14))] p-6 sm:p-8">
            <div className="inline-flex rounded bg-white/10 px-2 py-1 text-xs font-medium text-zinc-300">
              OFF AIR
            </div>
            <Heading level={3} className="mt-4 text-xl">
              Not currently live
            </Heading>
            <Text className="mt-2 max-w-2xl">
              {nextGame
                ? `Next scheduled game: ${fmtDateTime(nextGame.scheduledAt)} vs ${nextGame.opponent}.`
                : 'When this team goes live, the video will appear here.'}
            </Text>
          </div>
        </div>
      );
    }
    if (live.livePrivacy === 'PRIVATE' && !user) {
      return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-8 text-center">
          <Text className="text-zinc-100">This live stream is private.</Text>
          <Text className="mt-1 text-sm">Sign in to watch.</Text>
          <Button
            href={`/login?from=${encodeURIComponent(`/teams/${teamId ?? ''}`)}`}
            color="dark/zinc"
            className="mt-4"
          >
            Sign in
          </Button>
        </div>
      );
    }
    if (liveError) {
      return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-8 text-center text-red-400">
          {liveError}
        </div>
      );
    }
    if (!livePlaybackUrl) {
      return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="aspect-video w-full animate-pulse rounded-lg bg-zinc-950" />
          <Text className="mt-3 text-sm">Getting the live video ready...</Text>
        </div>
      );
    }
    const liveTitle = liveGame
      ? `${liveGame.homeOrAway === 'AWAY' ? '@' : 'vs'} ${liveGame.opponent}`
      : 'Live now';
    const player = live.gameId ? (
      <PlayerWithOverlay
        gameId={live.gameId}
        homeOrAway={liveGame?.homeOrAway}
        opponentName={liveGame?.opponent}
        teamName={team?.name}
      >
        <LivePlayer playbackUrl={livePlaybackUrl} />
      </PlayerWithOverlay>
    ) : (
      <LivePlayer playbackUrl={livePlaybackUrl} />
    );
    if (live.gameId) {
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-950/20 px-4 py-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge color="red">Live</Badge>
                <Text className="font-medium text-zinc-100">{liveTitle}</Text>
              </div>
              <Text className="mt-1 text-sm">Video is playing as close to real time as possible.</Text>
            </div>
            <Badge color={live.livePrivacy === 'PRIVATE' ? 'amber' : 'emerald'}>
              {live.livePrivacy === 'PRIVATE' ? 'Private' : 'Public'}
            </Badge>
          </div>
          {player}
        </div>
      );
    }
    return player;
  };

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <header className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950">
        <div className="h-2 bg-linear-to-r from-emerald-400 via-sky-400 to-zinc-700" />
        <div className="p-5 sm:p-6">
        <div>
          <TextLink href="/teams">← Teams</TextLink>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
              {team ? (
                <div className="flex items-center gap-4">
                  <div
                    className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-950 text-lg font-semibold text-zinc-300"
                    style={{
                      border: team.primaryColor ? `1px solid ${team.primaryColor}` : undefined,
                      color: team.secondaryColor ?? undefined,
                    }}
                  >
                    {team.logoUrl ? (
                      <img src={team.logoUrl} alt="" className="size-full object-cover" />
                    ) : (
                      team.name.slice(0, 2).toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0">
                    <Heading>{team.name}</Heading>
                    {team.season ? <Text className="mt-1">{team.season}</Text> : null}
                    {[team.leagueName, team.division, [team.city, team.state].filter(Boolean).join(', ')]
                      .filter(Boolean)
                      .length ? (
                      <Text className="mt-1 text-sm">
                        {[team.leagueName, team.division, [team.city, team.state].filter(Boolean).join(', ')]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Skeleton className="h-9 w-64" />
                  <Skeleton className="h-4 w-32" />
                </div>
              )}
            </div>
            {teamId ? <FollowButton teamId={teamId} /> : null}
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Live state</div>
              <div className="mt-1 text-lg font-semibold text-zinc-100">{live?.isLive ? 'On air now' : 'Off air'}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Next game</div>
              <div className="mt-1 text-sm font-medium text-zinc-100">{nextGame ? `vs ${nextGame.opponent}` : 'No game scheduled'}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Past games</div>
              <div className="mt-1 text-lg font-semibold text-zinc-100">{past.length} ready</div>
            </div>
          </div>
        </div>
        </div>
      </header>

      <section>
        <Subheading className="mb-3">Live</Subheading>
        {renderLive()}
      </section>

      {loadingGames ? (
        <section>
          <Subheading className="mb-3">Games</Subheading>
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        </section>
      ) : (
        <>
          {upcoming.length > 0 ? (
            <section>
              <Subheading className="mb-3">Upcoming</Subheading>
              <div className="grid gap-3 sm:grid-cols-2">
                {upcoming.map((g) => (
                  <GameCard key={g.gameId} game={g} teamId={teamId} />
                ))}
              </div>
            </section>
          ) : null}

          {events.length > 0 ? (
            <section>
              <Subheading className="mb-3">Team calendar</Subheading>
              <div className="grid gap-3 sm:grid-cols-2">
                {events.slice(0, 6).map((e) => (
                  <div key={e.eventId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                    <div className="font-medium text-zinc-100">{e.title}</div>
                    <Text className="mt-1 text-sm">
                      {fmtDateTime(e.startAt)}
                      {e.arrivalAt ? ` · arrive ${fmtDateTime(e.arrivalAt)}` : ''}
                      {e.locationName ? ` · ${e.locationName}` : ''}
                    </Text>
                    <Button href={`/teams/${teamId}/events/${e.eventId}`} outline className="mt-3">
                      Details
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section>
            <Subheading className="mb-3">Past games</Subheading>
            {past.length === 0 ? (
              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
                <Text>No past games yet.</Text>
                <Text className="mt-1 text-sm">
                  Videos will show up here after a game ends and the replay is ready.
                </Text>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {past.map((g) => (
                  <GameCard key={g.gameId} game={g} teamId={teamId} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {error ? <Text className="text-red-400">{error}</Text> : null}
    </div>
  );
}
